import { getScribeHttpClients } from '@heidi/api/client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { CPDSource } from '../../contracts';
import {
  CPD_HOMEPAGE_KEY,
  CPD_SAVED_SOURCES_KEY,
  CPD_SOURCES_INFINITE_KEY,
} from '../../contracts';
import type { APICPDSource } from './transform-source';
import { transformSource } from './transform-source';

// ============================================================================
// Transport
// ============================================================================

interface ScribeHttp {
  get<T>(url: string): Promise<{ data: T }>;
  post<T>(url: string, body?: unknown): Promise<{ data: T }>;
  delete<T>(url: string): Promise<{ data: T }>;
}

// Resolved PER CALL: the registry is filled at app bootstrap.
const http = (): ScribeHttp => getScribeHttpClients().scribeHttpV2;

// ============================================================================
// API Types
// ============================================================================

interface APISavedListResponse {
  sources: APICPDSource[];
  total: number;
}

interface APISaveResponse {
  source_id: string;
  saved: boolean;
  message: string;
}

interface APIUnsaveResponse {
  source_id: string;
  removed: boolean;
  message: string;
}

// ============================================================================
// API Functions
// ============================================================================

async function fetchSavedSources(): Promise<CPDSource[]> {
  const { data } = await http().get<APISavedListResponse>('/cpd/saved');
  return data.sources.map(transformSource);
}

async function apiSaveSource(sourceId: string): Promise<APISaveResponse> {
  const { data } = await http().post<APISaveResponse>(`/cpd/sources/${sourceId}/save`);
  return data;
}

async function apiUnsaveSource(sourceId: string): Promise<APIUnsaveResponse> {
  const { data } = await http().delete<APIUnsaveResponse>(
    `/cpd/sources/${sourceId}/save`
  );
  return data;
}

async function apiMarkAsRead(sourceId: string): Promise<void> {
  await http().post(`/cpd/sources/${sourceId}/read`);
}

async function apiMarkAsUnread(sourceId: string): Promise<void> {
  await http().delete(`/cpd/sources/${sourceId}/read`);
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to fetch saved CPD sources.
 */
export function useCPDSavedSources(isCpdQueryEnabled: boolean) {
  return useQuery({
    queryKey: [CPD_SAVED_SOURCES_KEY],
    queryFn: fetchSavedSources,
    staleTime: 30_000, // 30 seconds
    // SCR-4338: 30s staleness + mutation invalidation keep this fresh — don't also
    // join the tab-focus / network-reconnect refetch burst.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: isCpdQueryEnabled,
  });
}

/**
 * Hook providing save/unsave mutations.
 * Invalidates saved sources and homepage queries so UI stays in sync.
 */
export function useSaveSource() {
  const queryClient = useQueryClient();

  const invalidateAll = () => {
    void queryClient.invalidateQueries({ queryKey: [CPD_SAVED_SOURCES_KEY] });
    void queryClient.invalidateQueries({ queryKey: [CPD_HOMEPAGE_KEY] });
    void queryClient.invalidateQueries({ queryKey: [CPD_SOURCES_INFINITE_KEY] });
  };

  const save = useMutation({
    mutationFn: apiSaveSource,
    onMutate: async (sourceId) => {
      await queryClient.cancelQueries({ queryKey: [CPD_SAVED_SOURCES_KEY] });
      const previous = queryClient.getQueryData<CPDSource[]>([CPD_SAVED_SOURCES_KEY]);
      // Optimistically add to saved list (only id is needed for useSavedSourceIds lookup)
      if (previous && !previous.some((s) => s.id === sourceId)) {
        queryClient.setQueryData<CPDSource[]>(
          [CPD_SAVED_SOURCES_KEY],
          [...previous, { id: sourceId } as CPDSource]
        );
      }
      return { previous };
    },
    onError: (_err, _sourceId, context) => {
      if (context?.previous) {
        queryClient.setQueryData([CPD_SAVED_SOURCES_KEY], context.previous);
      }
    },
    onSettled: invalidateAll,
  });

  const unsave = useMutation({
    mutationFn: apiUnsaveSource,
    onMutate: async (sourceId) => {
      await queryClient.cancelQueries({ queryKey: [CPD_SAVED_SOURCES_KEY] });
      const previous = queryClient.getQueryData<CPDSource[]>([CPD_SAVED_SOURCES_KEY]);
      // Optimistically remove from saved list
      if (previous) {
        queryClient.setQueryData<CPDSource[]>(
          [CPD_SAVED_SOURCES_KEY],
          previous.filter((s) => s.id !== sourceId)
        );
      }
      return { previous };
    },
    onError: (_err, _sourceId, context) => {
      if (context?.previous) {
        queryClient.setQueryData([CPD_SAVED_SOURCES_KEY], context.previous);
      }
    },
    onSettled: invalidateAll,
  });

  return { save, unsave };
}

/**
 * Hook providing mark-as-read / mark-as-unread mutations with optimistic updates.
 *
 * Uses React Query's onMutate to instantly flip `is_read` in the cache so the
 * UI updates before the server responds. On error, rolls back to the snapshot.
 * On settle (success or error), invalidates all related queries to refetch
 * authoritative state from the backend.
 */
export function useMarkReadStatus() {
  const queryClient = useQueryClient();

  const invalidateAll = () => {
    void queryClient.invalidateQueries({ queryKey: [CPD_SAVED_SOURCES_KEY] });
    void queryClient.invalidateQueries({ queryKey: [CPD_HOMEPAGE_KEY] });
    void queryClient.invalidateQueries({ queryKey: [CPD_SOURCES_INFINITE_KEY] });
  };

  /** Optimistically flip is_read for a source in all infinite-query pages. */
  const flipReadInCache = (sourceId: string, isRead: boolean) => {
    queryClient.setQueriesData<{
      pages: { sources: { id: string; is_read?: boolean }[] }[];
      pageParams: unknown[];
    }>({ queryKey: [CPD_SOURCES_INFINITE_KEY] }, (old) => {
      if (!old) return old;
      return {
        ...old,
        pages: old.pages.map((page) => ({
          ...page,
          sources: page.sources.map((s) =>
            s.id === sourceId ? { ...s, is_read: isRead } : s
          ),
        })),
      };
    });
  };

  const markRead = useMutation({
    mutationFn: apiMarkAsRead,
    onMutate: async (sourceId) => {
      await queryClient.cancelQueries({ queryKey: [CPD_SOURCES_INFINITE_KEY] });
      const previous = queryClient.getQueriesData({
        queryKey: [CPD_SOURCES_INFINITE_KEY],
      });
      flipReadInCache(sourceId, true);
      return { previous };
    },
    onError: (_err, _sourceId, context) => {
      // Rollback to snapshot
      context?.previous?.forEach(([key, data]) =>
        queryClient.setQueryData(key as string[], data)
      );
    },
    onSettled: invalidateAll,
  });

  const markUnread = useMutation({
    mutationFn: apiMarkAsUnread,
    onMutate: async (sourceId) => {
      await queryClient.cancelQueries({ queryKey: [CPD_SOURCES_INFINITE_KEY] });
      const previous = queryClient.getQueriesData({
        queryKey: [CPD_SOURCES_INFINITE_KEY],
      });
      flipReadInCache(sourceId, false);
      return { previous };
    },
    onError: (_err, _sourceId, context) => {
      context?.previous?.forEach(([key, data]) =>
        queryClient.setQueryData(key as string[], data)
      );
    },
    onSettled: invalidateAll,
  });

  return { markRead, markUnread };
}

/**
 * Combined hook that checks if a source is saved (by ID).
 * Returns a set of saved source IDs for fast lookup.
 */
export function useSavedSourceIds(isCpdQueryEnabled: boolean): Set<string> {
  const { data } = useCPDSavedSources(isCpdQueryEnabled);
  if (!data) return new Set();
  return new Set(data.map((s) => s.id));
}
